import type {
  OrganizationRepository,
  OrganizationListOptions,
  OrganizationPage,
} from '#/domain/organization/OrganizationRepository'
import type { Organization } from '#/domain/organization/Organization'
import type { HttpClientPort } from '#/infrastructure/persistence/HttpClientPort'
import { objectToFormData } from '#/infrastructure/persistence/objectToFormData'
import {
  toDomain,
  toDomainFindById,
  toDomainPage,
  type OrganizationResponseDto,
  type OrganizationPageResponseDto,
} from './OrganizationDto'

// Auth0 caps per_page at 100.
const PAGE_SIZE = 100

export class HttpOrganizationRepository implements OrganizationRepository {
  constructor(private readonly http: HttpClientPort) {}

  async findAll(options: OrganizationListOptions): Promise<OrganizationPage> {
    const searchParams = new URLSearchParams({
      page: String(options.page),
      itemsPerPage: String(options.itemsPerPage),
    })
    const raw = await this.http.get<OrganizationPageResponseDto>('auth0/organization', {
      searchParams,
    })
    return toDomainPage(raw)
  }

  async findEvery(): Promise<OrganizationPage['items']> {
    const first = await this.findAll({ page: 1, itemsPerPage: PAGE_SIZE })
    const remaining = await this.findRemainingPages(first.total)
    return [first, ...remaining].flatMap((page) => page.items)
  }

  private findRemainingPages(total: number): Promise<OrganizationPage[]> {
    const remainingPagesArray = Array.from(
      { length: Math.max(0, Math.ceil(total / PAGE_SIZE) - 1) },
      (_, i) => i + 2,
    )
    return Promise.all(
      remainingPagesArray.map((page) =>
        this.findAll({ page, itemsPerPage: PAGE_SIZE }).catch((): OrganizationPage => ({
          items: [],
          total: 0,
        })),
      ),
    )
  }

  async findById(id: string): Promise<Organization> {
    const raw = await this.http.get<OrganizationResponseDto>(`auth0/organization/${id}`)
    return toDomainFindById(raw)
  }

  async create(organization: Organization): Promise<Organization> {
    const raw = await this.http.post<OrganizationResponseDto>('auth0/organization', {
      body: objectToFormData(organization),
    })
    return toDomain(raw)
  }

  async update(organization: Organization): Promise<Organization> {
    const raw = await this.http.patch<OrganizationResponseDto>('auth0/organization', {
      body: objectToFormData(organization),
    })
    return toDomain(raw)
  }

  remove(id: string): Promise<void> {
    return this.http.delete<void>(`auth0/organization/${id}`)
  }
}
